import React from 'react';
import { Link } from 'react-router-dom';

const APP_STORE_URL = 'https://apps.apple.com/app/mick-oc/id6772787701';

function PublicLayout({ eyebrow, title, children }) {
  return (
    <div className="min-h-screen bg-brand-offwhite px-4 py-8 text-brand-black">
      <div className="mx-auto max-w-4xl">
        <header className="flex flex-col gap-4 rounded-lg border border-stone-200 bg-white px-5 py-5 shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <Link to="/" aria-label="Mick OC home">
            <img src="/mick-oc-logo.png" alt="Mick OC" className="h-auto w-40 rounded-lg bg-brand-black" />
          </Link>
          <nav className="flex flex-wrap gap-3 text-sm font-semibold text-stone-700">
            <Link to="/privacy" className="hover:text-brand-orange">
              Privacy
            </Link>
            <Link to="/terms" className="hover:text-brand-orange">
              Terms
            </Link>
            <Link to="/support" className="hover:text-brand-orange">
              Support
            </Link>
            <Link to="/" className="hover:text-brand-orange">
              Sign in
            </Link>
          </nav>
        </header>

        <main className="mt-6 rounded-lg border border-stone-200 bg-white px-6 py-8 shadow-sm md:px-10">
          <p className="eyebrow">{eyebrow}</p>
          <h1 className="mt-3 text-4xl font-semibold">{title}</h1>
          <div className="mt-7 space-y-7 text-base leading-8 text-stone-700">{children}</div>
        </main>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-2xl font-semibold text-brand-black">{title}</h2>
      <div className="mt-3 space-y-3">{children}</div>
    </section>
  );
}

export function PrivacyPolicy() {
  return (
    <PublicLayout eyebrow="Privacy Policy" title="Mick OC Privacy Policy">
      <p>Last updated: September 22, 2026</p>

      <Section title="What Mick OC Does">
        <p>
          Mick OC helps football coaches manage a private team playbook, receive game-day run and
          pass recommendations from that playbook, and review called plays and coaching points.
        </p>
      </Section>

      <Section title="Information We Collect">
        <p>
          We collect account information such as coach name, email address, phone number when
          provided, school or organization name, team name, and password credentials.
        </p>
        <p>
          We also store team content entered into Mick OC, including playbook information, game
          situations, recommended plays, called-play history, coaching notes, results, and uploaded
          mascot or logo images.
        </p>
        <p>
          If you request a demo, we collect your name, email, school or team, coaching role, and
          preferred time to arrange and confirm the appointment by email.
        </p>
      </Section>

      <Section title="Microphone Use">
        <p>
          The mobile app may use the device microphone when a coach chooses to speak a game
          situation instead of typing it. Voice input is used to create text for the play call
          request. Coaches can use Mick OC without microphone input by typing the situation.
        </p>
      </Section>

      <Section title="How We Use Information">
        <p>
          We use account and team information to provide the Mick OC service, secure team access,
          generate play recommendations, show called-play history, support coaching review, process
          subscription billing, and respond to support requests.
        </p>
        <p>We may measure completed demo requests from advertising to understand campaign results.</p>
      </Section>

      <Section title="Payments">
        <p>
          Mick OC uses Stripe to process subscription payments. Mick OC does not store full credit
          card numbers on its servers.
        </p>
      </Section>

      <Section title="Sharing">
        <p>
          We do not sell team playbooks or coach information. We share information only with service
          providers needed to operate Mick OC, such as hosting, database, AI, analytics, and payment
          providers, or when required by law.
        </p>
      </Section>

      <Section title="Data Security">
        <p>
          We use reasonable safeguards to protect accounts and team information. No online service
          can guarantee perfect security, so coaches should use strong passwords and limit access to
          trusted staff.
        </p>
      </Section>

      <Section title="Contact">
        <p>
          Questions about this policy or Mick OC privacy practices can be sent to{' '}
          <a className="font-semibold text-brand-orange" href="mailto:davesystemsinc@gmail.com">
            davesystemsinc@gmail.com
          </a>
          .
        </p>
      </Section>
    </PublicLayout>
  );
}

export function TermsOfService() {
  return (
    <PublicLayout eyebrow="Terms" title="Mick OC Terms of Service">
      <p>Last updated: June 14, 2026</p>

      <Section title="Service">
        <p>
          Mick OC provides software for football coaches to manage playbook information, request
          game-day recommendations, and review called plays and coaching points.
        </p>
      </Section>

      <Section title="Coach Responsibility">
        <p>
          Mick OC is a coaching support tool. Coaches remain responsible for all game decisions,
          player safety, compliance with league rules, and use of recommendations.
        </p>
      </Section>

      <Section title="Team Content">
        <p>
          Teams are responsible for the accuracy and legality of the playbook, mascot, logo, and
          coaching content they upload. Teams should only upload content they have the right to use.
        </p>
      </Section>

      <Section title="Accounts">
        <p>
          The primary coach manages the team account and may add or remove coach logins. Account
          credentials should be kept private and shared only with authorized staff.
        </p>
      </Section>

      <Section title="Subscriptions">
        <p>
          Paid team subscriptions are processed through Stripe. Subscription terms, renewal timing,
          and payment details are shown during checkout.
        </p>
      </Section>

      <Section title="Availability">
        <p>
          We work to keep Mick OC available, but service may be interrupted by maintenance, internet
          issues, hosting providers, app store requirements, or third-party service outages.
        </p>
      </Section>

      <Section title="Contact">
        <p>
          Questions about these terms can be sent to{' '}
          <a className="font-semibold text-brand-orange" href="mailto:davesystemsinc@gmail.com">
            davesystemsinc@gmail.com
          </a>
          .
        </p>
      </Section>
    </PublicLayout>
  );
}

export function SupportPage() {
  return (
    <PublicLayout eyebrow="Support" title="Mick OC Support">
      <Section title="Get Help">
        <p>
          For account, billing, playbook, app, or game-day workflow support, email{' '}
          <a className="font-semibold text-brand-orange" href="mailto:davesystemsinc@gmail.com">
            davesystemsinc@gmail.com
          </a>
          .
        </p>
      </Section>

      <Section title="What To Include">
        <p>
          Include your school or team name, coach name, email address used for Mick OC, device type,
          and a short description of what happened.
        </p>
      </Section>

      <Section title="Production Site">
        <p>
          Coaches should use{' '}
          <a className="font-semibold text-brand-orange" href="https://mickoc.com">
            https://mickoc.com
          </a>{' '}
          to create accounts, manage playbooks, manage coaches, upload team logos, and review called
          plays.
        </p>
      </Section>

      <Section title="Mobile Apps">
        <p>
          iPhone coaches can install Mick OC from the{' '}
          <a
            className="font-semibold text-brand-orange"
            href={APP_STORE_URL}
            target="_blank"
            rel="noreferrer"
          >
            App Store
          </a>
          .
        </p>
        <p>
          Google Play release is pending final production approval. The Android link will be added
          here as soon as Google Play approves Mick OC for production.
        </p>
      </Section>
    </PublicLayout>
  );
}
